import type { AuftragsartRoute, TimesheetFlowScreen } from "@/features/timesheetFlow/timesheetFlowTypes";
import type { TimesheetProjectPreset } from "@/features/timesheetFlow/timesheetFlowTypes";
import { isFixedPriceOrderType } from "@/pages/projekte/hoursUsage";
import {
  buildLabelIndexFromSchemas,
  resolveCustomFieldByLabels,
} from "@/pages/projekte/hoursUsage";
import type { HeroCustomFieldSchema } from "@/lib/projects";

const ORDER_TYPE_LABELS = ["Auftragsart"];

function normalizeAuftragsart(value: unknown): string {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s\-_/]+/g, "");
}

export function isNwPlusFpAuftragsart(value: unknown): boolean {
  const n = normalizeAuftragsart(value);
  return (
    n === "nw+fp" ||
    n === "nwfp" ||
    n === "mischmasch" ||
    n === "nachweismitfestpreis"
  );
}

export function isFestpreisOnlyAuftragsart(value: unknown): boolean {
  const n = normalizeAuftragsart(value);
  return n === "festpreis" || n === "fp" || n === "festpreisauftrag";
}

export function isNachweisAuftragsart(value: unknown): boolean {
  const n = normalizeAuftragsart(value);
  return (
    n === "nachweis" ||
    n === "nw" ||
    n === "regie" ||
    n === "stundenlohn" ||
    n === "stundenlohnauftrag"
  );
}

export function resolveAuftragsartRoute(value: unknown): AuftragsartRoute {
  if (!value || !String(value).trim()) return "unknown";
  if (isFestpreisOnlyAuftragsart(value)) return "festpreis";
  if (isNwPlusFpAuftragsart(value) || isFixedPriceOrderType(value)) {
    if (isNwPlusFpAuftragsart(value)) return "mischmasch";
    if (isFestpreisOnlyAuftragsart(value)) return "festpreis";
  }
  if (isNachweisAuftragsart(value)) return "nachweis";
  return "nachweis";
}

/** Neuer Nachweis: Festpreis → Fertigstellungsmeldung, Lieferschein schreibt die GF. */
export function createScreenForAuftragsartRoute(
  route: AuftragsartRoute,
): Extract<TimesheetFlowScreen, "completionEditor" | "mischmaschChoice" | "timesheetEditor"> | null {
  if (route === "festpreis") return "completionEditor";
  if (route === "mischmasch") return "mischmaschChoice";
  if (route === "unknown") return null;
  return "timesheetEditor";
}

export function resolveProjectAuftragsart(
  project: TimesheetProjectPreset,
  schemas: HeroCustomFieldSchema[],
): string | null {
  if (project.auftragsart) return String(project.auftragsart);
  const fromApp = String(project.auftragsart_app || "").trim();
  if (fromApp) return fromApp;
  const idx = buildLabelIndexFromSchemas(schemas);
  const fromCf = resolveCustomFieldByLabels(
    project as Parameters<typeof resolveCustomFieldByLabels>[0],
    ORDER_TYPE_LABELS,
    idx,
  );
  return fromCf != null && String(fromCf).trim() ? String(fromCf) : null;
}

export function isFpComment(comment: unknown): boolean {
  return /\bfp\b/i.test(String(comment || ""));
}

export function canMarkHeroEntryAsFestpreis(entry: {
  hero_uuid?: unknown;
  comment?: unknown;
}): boolean {
  const heroUuid = String(entry.hero_uuid || "").trim();
  return Boolean(heroUuid) && !isFpComment(entry.comment);
}

export function appendFpToComment(comment: unknown): string | null {
  if (isFpComment(comment)) return null;
  const text = String(comment || "").trim();
  return text ? `${text} FP` : "FP";
}

export function heroTimeEntryHasFpMarker(entry: {
  comment?: unknown;
  category_name?: unknown;
}): boolean {
  return isFpComment(entry.comment) || isFpComment(entry.category_name);
}
